/**
 * Product View Panes — constants for layout panes that can be reordered/hidden
 * Used by layout schema from panel settings (unit_sheet_layout)
 */

/**
 * Panes available in product view.
 * HEADER and VIEWER are not reorderable on desktop — they're fixed to layout.
 * VIEWER is reorderable on mobile.
 * Other panes are reorderable and can be hidden via unit_sheet_layout settings.
 */
export const PRODUCT_VIEW_PANE = {
  HEADER: 'header', // Fixed: always top (mobile) or top of left column (desktop)
  VIEWER: 'viewer', // Plan rendering section. Always right column on desktop, reorderable on mobile
  SUMMARY: 'summary', // Product data summary
  PRICING: 'pricing', // Price + supplements + history
  INQUIRY: 'inquiry', // Call-to-action query button (sticky by default)
  ACTIONS: 'actions', // Additional action buttons i.e. share, save + defined by panel settings
  MAIN_DISCLAIMER: 'main_disclaimer', // Important disclaimer (near pricing/actions)
  DETAILS: 'details', // Product details/description (may vary by mode)
  MODE_DETAILS: 'mode_details', // Mode-specific details overlay on DETAILS
  MODE_CONTROLLER: 'mode_controller', // Mode selection/control section (desktop: pane; mobile: drawer)
  COMPARTMENTS: 'compartments', // Compartment list/details
  RESOURCES: 'resources', // Documents, downloads, etc.
}

export const PRODUCT_VIEW_PANE_LABEL_KEYS = {
  [PRODUCT_VIEW_PANE.HEADER]: 'product.pane_header',
  [PRODUCT_VIEW_PANE.VIEWER]: 'product.pane_viewer',
  [PRODUCT_VIEW_PANE.SUMMARY]: 'product.pane_summary',
  [PRODUCT_VIEW_PANE.PRICING]: 'product.pane_pricing',
  [PRODUCT_VIEW_PANE.INQUIRY]: 'product.pane_inquiry',
  [PRODUCT_VIEW_PANE.ACTIONS]: 'product.pane_actions',
  [PRODUCT_VIEW_PANE.COMPARTMENTS]: 'product.pane_compartments',
  [PRODUCT_VIEW_PANE.DETAILS]: 'product.pane_details',
  [PRODUCT_VIEW_PANE.MODE_DETAILS]: 'product.pane_mode_details',
  [PRODUCT_VIEW_PANE.MODE_CONTROLLER]: 'product.pane_mode_controller',
  [PRODUCT_VIEW_PANE.MAIN_DISCLAIMER]: 'product.pane_main_disclaimer',
  [PRODUCT_VIEW_PANE.RESOURCES]: 'product.pane_resources',
}

/**
 * Default pane order and configuration — fallback if not specified in panel settings (unit_sheet_layout).
 * HEADER is not included (it is fixed to layout).
 * Desktop: left column layout (VIEWER is always fixed right)
 * Mobile: full-width stacked layout
 */
export const PRODUCT_VIEW_PANE_DEFAULT_DESKTOP_ORDER = [
  { id: PRODUCT_VIEW_PANE.SUMMARY, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.PRICING, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.INQUIRY, is_sticky: true, is_visible: true },
  { id: PRODUCT_VIEW_PANE.MAIN_DISCLAIMER, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.DETAILS, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.MODE_DETAILS, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.MODE_CONTROLLER, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.COMPARTMENTS, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.RESOURCES, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.ACTIONS, is_sticky: false, is_visible: false },
]

export const PRODUCT_VIEW_PANE_DEFAULT_MOBILE_ORDER = [
  { id: PRODUCT_VIEW_PANE.VIEWER, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.SUMMARY, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.PRICING, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.MAIN_DISCLAIMER, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.INQUIRY, is_sticky: true, is_visible: true },
  { id: PRODUCT_VIEW_PANE.COMPARTMENTS, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.DETAILS, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.MODE_DETAILS, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.RESOURCES, is_sticky: false, is_visible: true },
  { id: PRODUCT_VIEW_PANE.ACTIONS, is_sticky: false, is_visible: false },
  // MODE_CONTROLLER on mobile is rendered as bottom drawer, not in order
]
